import * as React from "react";
import { LoginToUnlockDrawer } from "~/modules/dashboard_page_unlocks/components/LoginToUnlockDrawer";
import { UnlockPageDrawer } from "~/modules/dashboard_page_unlocks/components/UnlockPageDrawer";
import { useUser } from "~/modules/user/hooks/useUser";
import { api } from "~/utils/api";
import * as Analytics from "~/modules/dashboard_page_unlocks/analytics";
import { UpgradeToUnlockDrawer } from "../UpgradeToUnlockDrawer";

// // // //

/**
 * DashboardUnlockProvider
 * A boundary component that checks if the current page is unlocked and renders the children if it is.
 * If the page is not unlocked, it renders a paywall.
 */
function UnlockPageProvider(props: {
    pathname: string;
    organizationID: string;
    children: (childProps: {
        isFetching: boolean;
        remainingUnlockCount: number;
        isUnlocking: boolean;
        /** True when the unlock list failed to load (so `remainingUnlockCount` is unknown) */
        hasLoadError: boolean;
        errorMessage: string | null;
        onClickUnlock: () => void;
    }) => React.ReactNode;
}) {
    const { pathname, organizationID, children } = props;

    // Fetch the list of unlocked pages for the user's organization
    const {
        data,
        isLoading,
        isError: isLoadError,
        refetch,
    } = api.dashboardPageUnlocks.list.useQuery({
        organizationID: organizationID,
    });

    // If the list failed to load we don't know the remaining count, so we must not guess
    const hasLoadError = isLoadError && data === undefined;

    // Setup mutation to unlock the current page
    const unlockPageMutation = api.dashboardPageUnlocks.unlockPage.useMutation({
        onSuccess: () => {
            // NOTE - remainingUnlockCount is the pre-unlock value (from the last render)
            Analytics.trackPageUnlockSuccess({
                unlockPathname: pathname,
                organizationId: organizationID,
                remainingUnlockCount,
            });

            // Refetch the unlocked pages when the page is unlocked
            void refetch();
        },
        onError: (error) => {
            Analytics.trackPageUnlockError({
                unlockPathname: pathname,
                organizationId: organizationID,
                errorMessage: error.message,
            });
        },
    });

    // Pull unlocked pages + remaining unlock count from data
    const unlockedPages = data?.unlocked_pages ?? [];
    const remainingUnlockCount = data?.remaining_unlock_count ?? 0;

    // Check if the current page is unlocked
    const isUnlocked: boolean =
        unlockedPages.some((unlock) => unlock.pathname === pathname) ?? false;

    // If the page is unlocked -> don't render anything (nothing blocks page access)
    if (isUnlocked) {
        return null;
    }

    return (
        <>
            {children({
                isFetching: isLoading,
                hasLoadError,
                // NOTE - a load error is rendered by the drawer itself (with a reload link)
                errorMessage: unlockPageMutation.isError
                    ? "We couldn't unlock this page. Please try again."
                    : null,
                isUnlocking: unlockPageMutation.isPending,
                remainingUnlockCount,
                onClickUnlock: () => {
                    Analytics.trackPageUnlockAttempted({
                        unlockPathname: pathname,
                        organizationId: organizationID,
                        remainingUnlockCount,
                    });

                    // Unlock the current page
                    unlockPageMutation.mutateAsync({
                        pathname,
                        organizationID,
                    });
                },
            })}
        </>
    );
}

// // // //

/**
 * DashboardUnlockBoundary
 * A boundary component that checks if the current page is unlocked and renders the children if it is.
 * If the page is not unlocked, it renders a paywall.
 */
export function DashboardUnlockBoundary(props: { pathname: string }) {
    const { pathname } = props;
    const { user, loading, isLoggedIn, isPaidPlan } = useUser();

    // Wait for the user to load. Until then `isLoggedIn` is false, which would otherwise
    // mount (and record a gate view for) the login drawer for logged-in users
    if (loading) {
        return null;
    }

    // User is on a paid plan -> don't render anything (nothing blocks page access)
    if (isPaidPlan) {
        return null;
    }

    // User is NOT logged-in -> render LoginToUnlockDrawer
    if (!isLoggedIn) {
        return <LoginToUnlockDrawer />;
    }

    // User is NOT on a paid plan -> render UnlockPageProvider + UnlockPageDrawer or UpgradeToUnlockDrawer
    // NOTE - UnlockPageProvider will render null if the page is already unlocked
    return (
        <UnlockPageProvider
            pathname={pathname}
            organizationID={user.organization.id}
        >
            {({
                isFetching,
                isUnlocking,
                hasLoadError,
                errorMessage,
                remainingUnlockCount,
                onClickUnlock,
            }) => (
                <>
                    {/* If the page is not unlocked, and the user has remaining unlocks -> render UnlockPageDrawer */}
                    {(hasLoadError || remainingUnlockCount > 0) && (
                        <UnlockPageDrawer
                            organizationId={user.organization.id}
                            isFetching={isFetching}
                            isUnlocking={isUnlocking}
                            remainingUnlockCount={
                                hasLoadError ? undefined : remainingUnlockCount
                            }
                            hasLoadError={hasLoadError}
                            errorMessage={errorMessage}
                            onClickUnlock={onClickUnlock}
                        />
                    )}

                    {/* If the page is not unlocked, and the user does NOT have any remaining unlocks (and the unlock list has loaded) -> render UpgradeToUnlockDrawer */}
                    {!isFetching &&
                        !hasLoadError &&
                        remainingUnlockCount === 0 && (
                            <UpgradeToUnlockDrawer
                                organizationId={user.organization.id}
                            />
                        )}
                </>
            )}
        </UnlockPageProvider>
    );
}
